import React, { useEffect, useState, useRef } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Row, Col, Input
} from 'reactstrap';
import base from '../Services/BaseService';
import LoadSpinner from './Shared/Loading';
import { AiOutlineDelete } from 'react-icons/ai';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';

const InvestmentMultiModal = ({ closeModal, setSaveItem, investmentPots }) => {
    const [investmentRows, setInvestmentRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const inputRefs = useRef({});

    const toggleModal = () => {
        closeModal();
    };

    useEffect(() => {
        let isMounted = true; // flag to track mount status

        const fetchRows = async () => {
            const rows = await Promise.all(
                investmentPots.map(async (item) => {
                    const { fundName, accountId, accountName } = item;
                    try {
                        const res = await base.getAll(`Investment/ShareISA/${fundName}/${accountId}`);
                        return {
                            fundName,
                            accountId,
                            accountName,
                            bookCost: res?.bookCost || 0,
                            fundPrice: res?.fundPrice || 0,
                            fundValuation: res?.fundValuation || 0,
                            exists: true,
                        };
                    } catch {
                        return {
                            fundName,
                            accountId,
                            accountName,
                            bookCost: 0,
                            fundPrice: 0,
                            fundValuation: 0,
                            exists: false,
                            id: 0,
                        };
                    }
                })
            );
            if (isMounted) {
                setInvestmentRows(rows);
                setLoading(false);
            }
        };

        fetchRows();

        return () => {
            isMounted = false;
        };
    }, [investmentPots]);


    const handleFieldChange = (index, field, value) => {
        const updated = [...investmentRows];
        updated[index][field] = value;
        setInvestmentRows(updated);
    };

    const handleKeyDown = (e, rowIndex, field) => {
        const fields = ['bookCost', 'fundPrice', 'fundValuation'];
        const colIndex = fields.indexOf(field);
        let newRow = rowIndex;
        let newCol = colIndex;

        if (e.key === 'ArrowRight') newCol++;
        if (e.key === 'ArrowLeft') newCol--;
        if (e.key === 'ArrowDown') newRow++;
        if (e.key === 'ArrowUp') newRow--;

        if (
            newRow >= 0 && newRow < investmentRows.length &&
            newCol >= 0 && newCol < fields.length
        ) {
            const newField = fields[newCol];
            const refKey = `${newRow}-${newField}`;
            inputRefs.current[refKey]?.focus();
        }
    };

    const deleteRow = (index) => {
        const updated = [...investmentRows];
        updated.splice(index, 1);
        setInvestmentRows(updated);
    };

    const addNewRow = () => {
        setInvestmentRows([
            ...investmentRows,
            {
                fundName: '',
                accountId: '',
                accountName: '',
                bookCost: 0,
                fundPrice: 0,
                fundValuation: 0,
                exists: false,
                id: 0
            },
        ]);
    };

    const saveAll = async () => {
        try {
            for (const row of investmentRows) {
                if (!row.fundName || !row.accountId) continue;

                const payload = {
                    fundName: row.fundName,
                    accountId: row.accountId,
                    accountName: row.accountName,
                    bookCost: row.bookCost,
                    fundPrice: row.fundPrice,
                    fundValuation: row.fundValuation,
                };
                console.log(row.id, "Payload:", payload);

                await base.add("Investment", payload);
            }

            setSaveItem(Date.now());
            toggleModal();
        } catch (err) {
            console.error("Error saving investments:", err);
        }
    };

    const decimalRegex = /^[0-9]*([.][0-9]*)?$/; // allows "", "1", "1.", "1.2"

    const handleChange = (idx, field, raw) => {
    const v = raw.replace(',', '.');
    if (v === '' || decimalRegex.test(v)) {
        handleFieldChange(idx, field, v); 
    }
    };

    const handleBlur = (idx, field, v) => {
    // normalize on blur (e.g., "" -> "", "1." -> "1.0" or "1")
    const n = v === '' ? '' : String(Number(v));
    handleFieldChange(idx, field, n);
    };

    return (
        <Modal
            isOpen
            size="xl"
            toggle={toggleModal}
            backdrop="static"
            className={`manage-investments-modal${isFullscreen ? ' manage-investments-modal-fullscreen' : ''}`}
        >
            <ModalHeader className="bg-modal" toggle={toggleModal}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>Manage Investments</span>
                    <Button
                        color="link"
                        className="p-0 text-dark me-2"
                        onClick={() => setIsFullscreen(fullscreen => !fullscreen)}
                        aria-label={isFullscreen ? 'Exit full screen' : 'View full screen'}
                        title={isFullscreen ? 'Exit full screen' : 'View full screen'}
                    >
                        {isFullscreen ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
                    </Button>
                </div>
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <LoadSpinner />
                ) : (
                    <>
                        <Row className="mb-2 align-items-center">
                            <Col className="input-text mb-1" md="3">
                                Share/Fund
                            </Col>
                            <Col className="input-text mb-1" md="2">
                                AccountId
                            </Col>
                            <Col className="input-text mb-1" md="2">
                                Book Cost
                            </Col>
                            <Col className="input-text mb-1" md="2">
                                Fund Price
                            </Col>
                            <Col className="input-text mb-1" md="2">
                                Fund Valuation
                            </Col>
                        </Row>
                        {investmentRows.map((row, idx) => (

                            <Row key={idx} className="mb-2 align-items-center">
                                <Col md="3">
                                    <Input
                                        type="text"
                                        className="input-text mb-1"
                                        value={row.fundName}
                                        disabled={row.exists}
                                        onChange={(e) => handleFieldChange(idx, 'fundName', e.target.value)}
                                        placeholder="Fund Name"
                                    />

                                </Col>
                                <Col md="2">
                                    <Input
                                        type="text"
                                        className="input-text"
                                        value={row.accountId}
                                        disabled={row.exists}
                                        onChange={(e) => handleFieldChange(idx, 'accountId', e.target.value)}
                                        placeholder="Account Id"
                                    />
                                </Col>
                                {['bookCost', 'fundPrice', 'fundValuation'].map((field) => (
                                    <Col md="2" key={field}>
                                                                <Input
                                        type="text"              
                                        inputMode="decimal"
                                        className="input-text text-end"
                                        value={String(row[field] ?? '')}     // keep value as string to avoid caret jumps
                                        innerRef={el => (inputRefs.current[`${idx}-${field}`] = el)}
                                        onChange={e => handleChange(idx, field, e.target.value)}
                                        onBlur={e => handleBlur(idx, field, e.target.value)}
                                        onKeyDown={e => handleKeyDown(e, idx, field)}
                                        />

                                    </Col>
                                ))}
                                <Col md="1">
                                    <Button color="tertiary" size="sm" onClick={() => deleteRow(idx)}>
                                        <AiOutlineDelete size={18} />
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                        <Button size="sm" color="primary" className="btn-text b-width" onClick={addNewRow}>
                            Add Row
                        </Button>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" size="sm" className="btn-text b-width" onClick={toggleModal}>
                    Cancel
                </Button>
                <Button color="success" size="sm" className="btn-text b-width" onClick={saveAll}>
                    Save All
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default InvestmentMultiModal;
